/* Copyright (c) 2020-present CloudIO Inc */

import { atom, emptyFunction, useStateXAtomValue } from '@cloudio-saas/statex';
import {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { Platform } from '../../../componentregistry';
import useConstant from '../../../hooks/useConstant';
import { usePlatform } from '../../../platform/PlatformHooks';
import { isNull } from '../../../utils/isEmpty';
import { PubSub } from '../../../utils/PubSub';
import { useIsRTL } from '../../../utils/state';

interface EventMap {
  OnChange: {
    x?: number;
    y?: number;
    wheeling: boolean;
    scrolling: boolean;
  };
  UpdateScroll: {
    x: number;
    y: number;
  };
  OnComplete: boolean;
}

const scrollingAtom = atom({
  path: ['transient', 'scrolling'],
  defaultValue: false,
});

class ScrollValue {
  wheeling = false;
  scrolling = false;
  xMax = 0;
  yMax = 0;
  x = 0;
  y = 0;
  pubsub = new PubSub<EventMap>();
  timer?: NodeJS.Timeout;
  platform: Platform;

  constructor(platform: Platform) {
    this.platform = platform;
  }
  set = ({ x, y }: { x?: number; y?: number }) => {
    this.x = x ?? this.x;
    this.y = y ?? this.y;
    this.platform.setter(scrollingAtom, true);
    this.pubsub.pub('OnChange', {
      x,
      y,
      scrolling: this.scrolling,
      wheeling: this.wheeling,
    });

    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.platform.startAsyncTransition(async () => {
        this.scrolling = false;
        this.wheeling = false;
        this.platform.setter(scrollingAtom, false);
        this.pubsub.pub('OnComplete', true);
      }, emptyFunction);
    }, 500);
  };

  destroy = () => {
    this.pubsub.destroy();
    this.platform.setter(scrollingAtom, false);
    if (this.timer) clearTimeout(this.timer);
  };
}

function Wheel({
  children,
  height,
  width,
}: {
  children: ReactNode;
  height: number;
  width: number;
}) {
  const platform = usePlatform();
  const value = useScrollValue();
  const ref = useRef<HTMLDivElement>(null);
  function onWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (value.scrolling) return;

    let { deltaX = 0, deltaY = 0 } = e ?? {
      deltaX: 0,
      deltaY: 0,
    };

    if (deltaY !== 0 && value.yMax <= 0) {
      deltaY = 0;
    }
    if (deltaX !== 0 && value.xMax <= 0) {
      deltaX = 0;
    }

    if (deltaX === 0 && deltaY === 0) return;

    let x = Math.round(value.x - deltaX);
    let y = Math.round(value.y - deltaY);

    if (x > 0) x = 0;
    if (y > 0) y = 0;

    if (Math.abs(x) > value.xMax) {
      x = -value.xMax;
    }
    if (Math.abs(y) > value.yMax) {
      y = -value.yMax;
    }

    if (x > 0) {
      x = 0;
    }
    if (y > 0) {
      y = 0;
    }

    if (x === value.x && y === value.y) return;

    platform.setter(scrollingAtom, true);
    value.wheeling = true;
    value.set({ x, y });
  }

  const clientRef = useRef({ clientX: -1, clientY: -1 });

  function onTouch(e: React.TouchEvent<HTMLDivElement>) {
    if (value.scrolling) return;
    const client = clientRef.current;
    let deltaX = 0;
    let deltaY = 0;
    if (e.type === 'touchstart') {
      if (e.touches.length) {
        const touch = e.touches[0];
        client.clientX = touch.clientX;
        client.clientY = touch.clientY;
      }
    } else if (e.type === 'touchmove') {
      if (e.changedTouches.length) {
        const touch = e.changedTouches[0];
        deltaY = Math.round(client.clientY - touch.clientY);
        if (deltaY > 100) {
          // make it faster
          deltaY = Math.round(deltaY * (deltaY / 100));
        }
        deltaX = Math.round(client.clientX - touch.clientX);
        client.clientX = touch.clientX;
        client.clientY = touch.clientY;
      }
    } else if (e.type === 'touchend') {
      client.clientX = -1;
      client.clientY = -1;
    }

    if (deltaY !== 0 && value.yMax <= 0) {
      deltaY = 0;
    }
    if (deltaX !== 0 && value.xMax <= 0) {
      deltaX = 0;
    }
    if (deltaX === 0 && deltaY === 0) return;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      deltaY = 0;
    } else {
      deltaX = 0;
    }

    // Set absolute positions
    let x = value.x - deltaX;
    let y = value.y - deltaY;

    if (Math.abs(x) > value.xMax) x = -value.xMax;
    if (Math.abs(y) > value.yMax) y = -value.yMax;

    if (x > 0) {
      x = 0;
    }
    if (y > 0) {
      y = 0;
    }

    if (x === value.x && y === value.y) return;

    platform.setter(scrollingAtom, true);
    value.wheeling = true;
    value.set({ x, y });
  }

  return (
    <div
      className="wheel"
      style={{ width, height }}
      ref={ref}
      onWheel={onWheel}
      onTouchStart={onTouch}
      onTouchMove={onTouch}
      onTouchEnd={onTouch}
    >
      {children}
    </div>
  );
}

const Context = createContext<ScrollValue | undefined>(undefined);

function useScrollValue(): ScrollValue {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error('useScrollValue must be used within ScrollValueProvider');
  }
  return context;
}

function ScrollValueProvider({
  children,
  height,
  width,
  totalRows,
  itemId,
}: {
  children: ReactNode;
  height: number;
  width: number;
  totalRows: number;
  itemId: string;
}) {
  const platform = usePlatform();
  const value = useConstant(() => new ScrollValue(platform));
  const ref = useRef(totalRows);
  const refId = useRef(itemId);

  useEffect(() => {
    if (ref.current > totalRows && refId.current === itemId) {
      // total rows reduced... must be due to filtering
      // hence reset the vertical scroll bar position
      // this is causing the grid to reset to top when collapsing a tree column
      // TODO: fix this for store changes due to filtering
      // value.set({ y: 0 });
    }
    ref.current = totalRows;
    refId.current = itemId;
  }, [itemId, platform, totalRows, value]);
  useEffect(() => {
    return () => {
      value.destroy();
    };
  }, [value]);
  return (
    <Context.Provider value={value}>
      <Wheel width={width} height={height}>
        {children}
      </Wheel>
    </Context.Provider>
  );
}

function addDomEvent(
  target: EventTarget,
  eventName: string,
  handler: EventListener,
  options?: AddEventListenerOptions,
) {
  target.addEventListener(eventName, handler, options);

  return () => target.removeEventListener(eventName, handler, options);
}

const getElementScrollXOffsets =
  (element: HTMLElement, isRTL: boolean) => () => {
    return {
      x: isRTL ? -element.scrollLeft : element.scrollLeft,
    };
  };

const getElementScrollYOffsets = (element: HTMLElement) => () => {
  return {
    y: element.scrollTop,
    yMaxOffset: element.scrollHeight - element.offsetHeight,
  };
};

interface ScrollXOffsets {
  x: number;
}

type GetScrollXOffsets = () => ScrollXOffsets;

interface ScrollYOffsets {
  y: number;
}

type GetScrollYOffsets = () => ScrollYOffsets;

function createScrollXUpdater(
  values: ScrollValue,
  getOffsets: GetScrollXOffsets,
  platform: Platform,
) {
  const update = () => {
    const { x } = getOffsets();
    if (values.wheeling || Math.abs(x) === Math.abs(values.x)) {
      return;
    }

    values.scrolling = true;
    platform.setter(scrollingAtom, true);
    values.set({ x: -x });
  };

  update();

  return update;
}

function createScrollYUpdater(
  values: ScrollValue,
  getOffsets: GetScrollYOffsets,
  platform: Platform,
) {
  const update = () => {
    const { y } = getOffsets();
    if (values.wheeling || Math.abs(y) === Math.abs(values.y)) return;

    values.scrolling = true;
    platform.setter(scrollingAtom, true);
    values.set({ y: -y });
  };

  update();

  return update;
}

function useScroll(ref: RefObject<HTMLElement>, horizontal: boolean): void {
  const values = useScrollValue();
  const isRTL = useIsRTL();
  const platform = usePlatform();

  useEffect(() => {
    return values.pubsub.sub(
      'OnChange',
      async (_, { x, y, wheeling, scrolling }) => {
        if (ref.current && !scrolling && wheeling) {
          if (horizontal && !isNull(x)) {
            ref.current.scrollLeft = isRTL ? x : -x;
          } else if (!(horizontal || isNull(y))) {
            ref.current.scrollTop = -y;
          }
        }
      },
    );
  }, [values, ref, horizontal, isRTL]);
  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) return;

    const updateScrollValues = horizontal
      ? createScrollXUpdater(
          values,
          getElementScrollXOffsets(element, isRTL),
          platform,
        )
      : createScrollYUpdater(
          values,
          getElementScrollYOffsets(element),
          platform,
        );

    const scrollListener = addDomEvent(element, 'scroll', updateScrollValues, {
      passive: true,
    });

    const resizeListener = addDomEvent(element, 'resize', updateScrollValues);

    return () => {
      scrollListener();
      resizeListener();
    };
  }, [horizontal, isRTL, platform, ref, values]);
}

function useIsScrolling(): boolean {
  return useStateXAtomValue(scrollingAtom);
}

export { useIsScrolling, ScrollValueProvider, useScrollValue, useScroll };
